// Draw a diagram inside the Whiteboard — opened from the "Diagram" button on the edgeless
// canvas, exactly like the "Crop image" flow (CropModal). The user draws in the self-hosted
// draw.io editor (all the Lucidchart-style shape/stencil + icon libraries); "Insert" exports
// the diagram as an SVG that embeds the editable draw.io XML, and the caller drops it onto the
// whiteboard as an image (persisted by BlockSuite like any other image — no new backend).
import React, { useRef, useState } from 'react';
import { DrawioEditor, DrawioHandle } from './DrawioEditor';

function dataUriToBlob(uri: string): Blob {
  const comma = uri.indexOf(',');
  const head = uri.slice(0, comma);
  const body = uri.slice(comma + 1);
  const mime = /data:([^;]+)/.exec(head)?.[1] || 'image/svg+xml';
  if (/;base64/i.test(head)) {
    const bin = atob(body);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return new Blob([bytes], { type: mime });
  }
  return new Blob([decodeURIComponent(body)], { type: mime });
}

export function DrawioModal({
  initialXml,
  dark,
  onInsert,
  onClose,
}: {
  initialXml?: string;
  dark?: boolean;
  onInsert: (blob: Blob) => void | Promise<void>;
  onClose: () => void;
}): React.ReactElement {
  const handleRef = useRef<DrawioHandle | null>(null);
  const [busy, setBusy] = useState(false);

  // draw.io answers our export request with {event:'export', data:<data-uri>}.
  const handleExport = async (data: string): Promise<void> => {
    try {
      const blob = data.startsWith('data:') ? dataUriToBlob(data) : new Blob([data], { type: 'image/svg+xml' });
      await onInsert(blob);
      onClose();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[diagram] insert failed', e);
      window.alert('Could not insert the diagram onto the whiteboard.');
      setBusy(false);
    }
  };

  const insert = (): void => {
    if (!handleRef.current || busy) return;
    setBusy(true);
    handleRef.current.requestExport('xmlsvg'); // SVG (tight, explicit px size) + embedded editable XML
  };

  return (
    <div className="fx-modal-backdrop" onClick={onClose}>
      <div className="fx-modal fx-drawio-modal" onClick={(e) => e.stopPropagation()}>
        <div className="fx-drawio-modal-head">
          <div className="fx-login-title">Diagram</div>
          <div className="fx-drawio-modal-actions">
            <button type="button" className="fx-sendbtn" onClick={insert} disabled={busy}>
              {busy ? 'Inserting…' : 'Insert to whiteboard'}
            </button>
            <button type="button" className="fx-drawio-close" onClick={onClose} title="Close" aria-label="Close">×</button>
          </div>
        </div>
        <div className="fx-drawio-modal-body">
          <DrawioEditor
            xml={initialXml || ''}
            dark={dark}
            onSave={() => {}}
            onExport={(d) => void handleExport(d)}
            onReady={(h) => { handleRef.current = h; }}
            className="fx-drawio-frame"
          />
        </div>
      </div>
    </div>
  );
}
